// Leave Management Types
export interface Employee {
  id: number;
  employeeId: string;
  username: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  initials?: string;
  email: string;
  birthDate?: string;
  homeAddress?: string;
  personalPhone?: string;
  secondaryPhone?: string;
  emergencyContactName?: string;
  emergencyContactRelationship?: string;
  emergencyContactPhone?: string;
  emergencyContactName2?: string;
  emergencyContactRelationship2?: string;
  emergencyContactPhone2?: string;
  jobTitle?: string;
  department?: string;
  officeLocation?: string;
  hireDate: string;
  startDate?: string;
  endDate?: string;
  status: EmployeeStatus;
  managerId?: number;
  managerName?: string;
  roles?: string[];
  yearsOfService?: number;
  lastLoginAt?: string;
  version?: number;
}

export enum EmployeeStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  TERMINATED = "TERMINATED",
}

export interface LeaveType {
  id: number;
  code: string;
  name: string;
  description?: string;
  isPaid: boolean;
  requiresApproval: boolean;
  maxDaysPerYear?: number;
  isSystemManaged: boolean;
  isActive: boolean;
}

export interface LeaveBalance {
  id: number;
  employeeId: number;
  leaveTypeId: number;
  leaveTypeName: string;
  leaveTypeCode: string;
  year: number;
  allocatedDays: number;
  usedDays: number;
  availableDays: number;
  carriedOverDays: number;
  carriedOverFromYear?: number;
  expiryDate?: string;
}

export interface LeaveRequest {
  id: number;
  employeeId: number;
  employeeName: string;
  leaveTypeId: number;
  leaveTypeName: string;
  leaveTypeCode: string;
  startDate: string;
  endDate: string;
  totalDays: number;
  reason?: string;
  isHalfDay?: boolean;
  halfDayPeriod?: "MORNING" | "AFTERNOON";
  status: LeaveRequestStatus;
  approverId?: number;
  approverName?: string;
  approvedAt?: string;
  denialReason?: string;
  /** Indicates whether the leave was planned (true) or unplanned (false). NULL for pending or legacy requests. */
  isPlanned?: boolean | null;
  createdAt: string;
  updatedAt: string;
}

export enum LeaveRequestStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  DENIED = "DENIED",
  CANCELLED = "CANCELLED",
}

export interface CreateLeaveRequest {
  leaveTypeId: number;
  startDate: string;
  endDate: string;
  reason?: string;
  isHalfDay?: boolean;
  halfDayPeriod?: "MORNING" | "AFTERNOON";
  timezone?: string; // Optional timezone for date conversion
}

export interface LeaveRequestAction {
  actionType: "APPROVE" | "DENY" | "CANCEL";
  reason?: string;
}

export interface LeaveCalendarEvent {
  id: number;
  title: string;
  start: string; // Date string (YYYY-MM-DD) from backend
  end: string; // Date string (YYYY-MM-DD) from backend
  color: string;
  status: string;
  employeeName: string;
  leaveTypeName: string;
  totalDays: number;
  reason?: string;
  isHalfDay?: boolean;
  halfDayPeriod?: "MORNING" | "AFTERNOON";
}

export interface EmployeeDashboard {
  employee: Employee;
  currentYearBalances: LeaveBalance[];
  recentRequests: LeaveRequest[];
  pendingRequestsCount: number;
  upcomingLeave: LeaveRequest[];
}

export interface ApproverDashboard {
  pendingRequests: LeaveRequest[];
  teamRequests: LeaveRequest[];
  teamCalendar: LeaveCalendarEvent[];
  statistics: {
    totalPending: number;
    totalApproved: number;
    totalDenied: number;
  };
}

export interface AdminDashboard {
  totalEmployees: number;
  totalVacationAllocated: number;
  totalVacationUsed: number;
  totalVacationAvailable: number;
  averageVacationBalance: number;
  totalRequestsThisYear: number;
  pendingRequests: number;
  approvedRequests: number;
  deniedRequests: number;
  plannedRequests: number;
  unplannedRequests: number;
  currentYear: number;
}

export interface UserWithRoles {
  id: number;
  username: string;
  employeeId: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  ldapInitials?: string;
  email: string;
  birthDate?: string;
  homeAddress?: string;
  personalPhone?: string;
  secondaryPhone?: string;
  emergencyContactName?: string;
  emergencyContactRelationship?: string;
  emergencyContactPhone?: string;
  emergencyContactName2?: string;
  emergencyContactRelationship2?: string;
  emergencyContactPhone2?: string;
  jobTitle?: string;
  department?: string;
  officeLocation?: string;
  hireDate?: string;
  startDate?: string;
  endDate?: string;
  status: EmployeeStatus;
  roles: UserRole[];
  lastLoginAt?: string;
  lockedUntil?: string;
  lockCount?: number;
  version?: number;
}

export enum UserRole {
  EMPLOYEE = "EMPLOYEE",
  APPROVER = "APPROVER",
  ADMIN = "ADMIN",
}

export interface RoleInfo {
  role: UserRole;
  displayName: string;
  description: string;
}

export interface BulkOperationResult {
  totalProcessed: number;
  successful: number;
  failed: number;
  errors: string[];
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// Permission checking utilities
export interface UserPermissions {
  canSubmitLeave: boolean;
  canApproveLeave: boolean;
  canManageUsers: boolean;
  canViewReports: boolean;
  canAdjustBalances: boolean;
  canManageSystem: boolean;
}

export const getUserPermissions = (roles: string[]): UserPermissions => {
  const hasRole = (role: string) => roles.some((r) => r.includes(role));

  return {
    canSubmitLeave:
      hasRole("EMPLOYEE") || hasRole("APPROVER") || hasRole("ADMIN"),
    canApproveLeave: hasRole("APPROVER") || hasRole("ADMIN"),
    canManageUsers: hasRole("ADMIN"),
    canViewReports: hasRole("APPROVER") || hasRole("ADMIN"),
    canAdjustBalances: hasRole("ADMIN"),
    canManageSystem: hasRole("ADMIN"),
  };
};

// Date utilities for leave management
export const formatDate = (date: string | Date | any): string => {
  if (!date) {
    return "N/A";
  }

  try {
    let parsedDate: Date;

    if (Array.isArray(date)) {
      // Handle array format [year, month, day] or [year, month, day, hour, minute, second]
      const [year, month, day] = date;
      parsedDate = new Date(year, month - 1, day); // month is 0-based in JS
    } else if (typeof date === "string") {
      // Handle ISO string format
      parsedDate = new Date(date);
    } else if (date instanceof Date) {
      parsedDate = date;
    } else {
      parsedDate = new Date(date);
    }

    if (isNaN(parsedDate.getTime())) {
      throw new Error("Invalid date");
    }

    return parsedDate.toLocaleDateString(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch (error) {
    console.warn("Invalid date format received:", date, error);
    return "Invalid Date";
  }
};

export const formatDateTime = (date: string | Date | any): string => {
  if (!date) {
    return "N/A";
  }

  try {
    let parsedDate: Date;

    if (Array.isArray(date)) {
      // Handle array format [year, month, day, hour, minute, second]
      const [year, month, day, hour = 0, minute = 0, second = 0] = date;
      parsedDate = new Date(year, month - 1, day, hour, minute, second);
    } else if (typeof date === "string") {
      // Handle ISO string format
      parsedDate = new Date(date);
    } else if (date instanceof Date) {
      parsedDate = date;
    } else {
      parsedDate = new Date(date);
    }

    if (isNaN(parsedDate.getTime())) {
      throw new Error("Invalid date");
    }

    return parsedDate.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch (error) {
    console.warn("Invalid date format received:", date, error);
    return "Invalid Date";
  }
};

export const calculateDaysBetween = (
  startDate: string,
  endDate: string,
): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
};

// Status color utilities
export const getStatusColor = (status: LeaveRequestStatus): string => {
  switch (status) {
    case LeaveRequestStatus.PENDING:
      return "#ff9800"; // Orange
    case LeaveRequestStatus.APPROVED:
      return "#4caf50"; // Green
    case LeaveRequestStatus.DENIED:
      return "#f44336"; // Red
    case LeaveRequestStatus.CANCELLED:
      return "#9e9e9e"; // Grey
    default:
      return "#9e9e9e";
  }
};

export const getStatusIcon = (status: LeaveRequestStatus): string => {
  switch (status) {
    case LeaveRequestStatus.PENDING:
      return "schedule";
    case LeaveRequestStatus.APPROVED:
      return "check_circle";
    case LeaveRequestStatus.DENIED:
      return "cancel";
    case LeaveRequestStatus.CANCELLED:
      return "block";
    default:
      return "help";
  }
};
